import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';
import {useCallback} from 'react';

import {getQueryKey} from '../lib/helpers/queryKeys';
import {supabase} from '../lib/utils/supabaseClass';
import {Database} from '../types/database.types';
import {limitT} from '../types/pagination.types';
import {
  BaseRpcFilterT,
  NotRpcFilterT,
  OrRpcFilterT,
  PaginationKeyT,
  RpcFilterT,
  RpcReturnRowT,
  RpcSortOptionT,
} from '../types/query.types';
import {FlattenT} from '../types/utility.types';

/*********************************************************************
 * Helper Functions
 *********************************************************************/
// Function to validate the paginationKey
function isPaginationKey(value: string): value is PaginationKeyT {
  return value === 'all' || /^page:\d+ limit:\d+$/.test(value);
}

/*******************************************************************
 * Types
 *******************************************************************/
export type UseRpcQueryResponseT<F extends keyof Database['public']['Functions']> = {
  data: Database['public']['Functions'][F]['Returns'];
  total: number;
  currentPage: number;
  totalPages: number;
};

export type UseRpcQueryPropsT<
  F extends keyof Database['public']['Functions'],
  A extends Database['public']['Functions'][F]['Args'],
  TData = UseRpcQueryResponseT<F>,
  TError extends Error = Error,
  TQueryFnData = UseRpcQueryResponseT<F>,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'queryHash' | 'queryKeyHashFn'
  > & {
    functionName: F;
    functionArgs?: A;
    enabled?: boolean;
    page?: number;
    limit?: limitT;
    filters?: RpcFilterT<F>[];
    sortOption?: RpcSortOptionT<F>;
    searchTerm?: string;
    searchColumn?: keyof RpcReturnRowT<F>;
    all?: boolean;
  }
>;

export type CallFunctionPropsT<
  F extends keyof Database['public']['Functions'],
  A extends Database['public']['Functions'][F]['Args'],
> = {
  functionName: F;
  args: A;
  filters: RpcFilterT<F>[];
  sortOption?: RpcSortOptionT<F>;
  searchTerm?: string;
  searchColumn?: keyof RpcReturnRowT<F>;
  page?: number | null;
  limit?: limitT | null;
  all?: boolean;
};

/**
 * Calls a function from the database.
 * @template {Database['public']['Functions']} F - The function to call
 * @param {F} functionName - The function to call
 * @returns {Promise<UseRpcQueryResponseT<F>>} - The data from the function
 */
async function callFunction<
  F extends keyof Database['public']['Functions'],
  A extends Database['public']['Functions'][F]['Args'],
>({
  functionName,
  args,
  filters = [],
  sortOption,
  searchTerm = '',
  searchColumn,
  page = null,
  limit = null,
  all = !page && !limit,
}: CallFunctionPropsT<F, A>) {
  let query = supabase.rpc(functionName, {...args}, {count: 'exact'});

  // Apply search if searchTerm and searchColumn are provided
  if (searchTerm && searchColumn) {
    query = query.ilike(String(searchColumn), `%${searchTerm}%`);
  }

  // Only apply filters if they exist and have length
  if (filters && filters.length > 0) {
    filters.forEach(filter => {
      // Type guard to determine the type of filter
      function isBaseFilter(filter: RpcFilterT<F>): filter is BaseRpcFilterT<F> {
        return 'column' in filter && 'operator' in filter && 'value' in filter;
      }

      function isNotFilter(filter: RpcFilterT<F>): filter is NotRpcFilterT<F> {
        return 'not' in filter;
      }

      function isOrFilter(filter: RpcFilterT<F>): filter is OrRpcFilterT<F> {
        return 'or' in filter;
      }

      // Handle different filter types (similar to useDataQuery)
      if (isNotFilter(filter) && filter.not) {
        const {column, operator, value} = filter.not;
        if (!column || !operator) throw new Error('Column is required for NOT filter');
        query = query.not(String(column), operator, value);
      } else if (isOrFilter(filter) && filter.or) {
        const orConditions = filter.or
          .map(orFilter => {
            const {column: orColumn, operator: orOperator, value: orValue} = orFilter;
            if (!orColumn || !orOperator) throw new Error('Column, operator, and value are required for OR filter');
            return `${String(orColumn)} ${orOperator} ${orValue}`;
          })
          .join(' OR ');
        query = query.or(orConditions);
      } else if (isBaseFilter(filter)) {
        const {column, operator, value} = filter;
        if (!column || !operator) throw new Error('Column, operator, and value are required for filter');

        const columnString = String(column);

        switch (operator) {
          case 'eq':
            if (value === null || value === undefined)
              throw new Error('Value cannot be null or undefined for EQ operator, use IS operator instead');
            // @ts-ignore - TODO: fix this, value cannot be correctly typed
            query = query.eq(columnString, value);
            break;
          case 'neq':
            // @ts-ignore - TODO: fix this, value cannot be correctly typed
            query = query.neq(columnString, value);
            break;
          case 'gt':
            query = query.gt(columnString, value);
            break;
          case 'gte':
            query = query.gte(columnString, value);
            break;
          case 'lt':
            query = query.lt(columnString, value);
            break;
          case 'lte':
            query = query.lte(columnString, value);
            break;
          case 'like':
            query = query.like(columnString, String(value));
            break;
          case 'ilike':
            query = query.ilike(columnString, String(value));
            break;
          case 'in':
            if (!Array.isArray(value)) throw new Error('Value must be an array for IN operator');
            // @ts-ignore - TODO: fix this, value cannot be correctly typed
            query = query.in(columnString, value);
            break;
          case 'is':
            if (value === null || typeof value === 'boolean') {
              query = query.is(columnString, value);
            } else {
              throw new Error(
                'Value must be null or boolean for IS operator, use EQ operator for other types of values'
              );
            }
            break;
          default:
            throw new Error(`Unsupported operator: ${operator}`);
        }
      }
    });
  }

  if (sortOption) {
    // Apply sorting
    query = query.order(String(sortOption?.column), {ascending: sortOption?.direction === 'asc'});
  }

  // Apply pagination if not fetching all
  if (!all) {
    if (page !== null && limit !== null) {
      query = query.range((page - 1) * limit, page * limit - 1);
    }
  }

  const {data, error, count} = await query;

  if (error) {
    throw new Error(`Error fetching ${functionName}: ${error.message}`);
  }

  const totalPages = limit ? Math.ceil((count ?? 0) / limit) : 1;

  const response: UseRpcQueryResponseT<F> = {
    data: data,
    total: count ?? 0,
    totalPages: totalPages ?? 1,
    currentPage: page ?? 1,
  };

  return response;
}

/**
 * Calls a function from the database.
 * @param {F} functionName - The function to call
 * @param {boolean} enabled - Whether the query is enabled
 * @param {boolean} protect - Whether the query is protected
 * @returns {Promise<UseRpcQueryResponseT<F>>}
 */
export const useRpcQuery = <
  F extends keyof Database['public']['Functions'],
  A extends Database['public']['Functions'][F]['Args'],
>({
  functionName,
  functionArgs = {} as A,
  enabled = true,
  filters,
  page = 1,
  limit = 5,
  all = (!page && !limit) ?? true,
  sortOption,
  searchTerm = '',
  searchColumn,
  ...rest
}: UseRpcQueryPropsT<F, A>): ReturnType<
  typeof useQuery<UseRpcQueryResponseT<F>, Error, UseRpcQueryResponseT<F>, QueryKey>
> & {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
} => {
  const paginationKey = page !== undefined && limit !== undefined ? `page:${page} limit:${limit}` : 'all';
  if (!isPaginationKey(paginationKey)) throw new Error('Invalid pagination key');

  // Get the first available sort option or undefined
  const defaultSort = useCallback((): RpcSortOptionT<F> | undefined => {
    const returnType = {} as RpcReturnRowT<F>;
    const firstField = Object.keys(returnType)[0] as keyof RpcReturnRowT<F>;
    return firstField
      ? {
          column: firstField,
          direction: 'desc' as const,
          label: String(firstField),
        }
      : undefined;
  }, []);

  const effectiveSortOption = sortOption ?? defaultSort();

  const queryFn: QueryFunction<UseRpcQueryResponseT<F>, QueryKey> = async () => {
    return callFunction<F, A>({
      functionName,
      args: functionArgs,
      filters: filters || [],
      sortOption: effectiveSortOption,
      searchTerm,
      searchColumn,
      page,
      limit,
      all,
    });
  };

  const queryOptions: UseQueryOptions<UseRpcQueryResponseT<F>, Error, UseRpcQueryResponseT<F>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey: getQueryKey.function(functionName, {
      ...functionArgs,
      filters: filters || [],
      sortOption,
      searchTerm,
      searchColumn,
      page,
      limit,
    }),
    queryFn,
  };

  const queryResult = useQuery(queryOptions);

  return {
    ...queryResult,
    pagination: {
      currentPage: queryResult.data?.currentPage ?? 1,
      totalPages: queryResult.data?.totalPages ?? 1,
      totalItems: queryResult.data?.total ?? 0,
    },
  };
};
